import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import TokenDisplay from './TokenDisplay';
import QualAPI from 'utils/QualAPI';
import { useSignOut } from 'hooks/auth/mutations';

const PasswordResetContainer = (props) => {
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null); // reset_password_token (mobile flow)
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const location = useLocation();
    const history = useHistory();

    // Extract reset_password_token and mfa param from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        // Get reset token (mobile flow)
        const resetToken = params.get('reset_password_token');
        setToken(resetToken);

        // Check MFA from URL param (set by email link)
        setMfaRequired(params.get('mfa') === '1');

        // Clear URL params only if still on the reset page
        // (the deferred useEffect can fire after navigation to /sign_in?reset=true,
        // which would incorrectly strip the reset query param)
        if (location.pathname === '/reset') {
            history.replace({ search: '' });
        }
    }, []);

    const signOut = useSignOut();
    
    const resetPassword = useCallback((newPassword, confirmPassword, _) => {
        QualAPI.resetPassword(token, newPassword, confirmPassword, mfaCode || null)
            .then(async () => {
                await signOut.mutateAsync();
                // Hard redirect instead of history.push to avoid a race condition:
                // signOut invalidates auth state, which causes AppBody to re-render
                // with isLoggedIn=false. The catch-all <Redirect to="/sign_in" /> in
                // AppBody fires during this transition, stripping the ?reset=true
                // query param before LoginDisplay can read it. A full page load
                // bypasses React Router entirely and also ensures all cached auth
                // state is cleared — both desirable after sign-out.
                window.location.href = '/sign_in?reset=true';
            })
            .catch((error) => {
                const responseData = error?.response?.data;

                // Handle MFA required response
                if (responseData?.mfa_required) {
                    setMfaRequired(true);
                    const mfaError = responseData?.errors?.[0];
                    if (mfaError) {
                        setError({ error: true, message: mfaError });
                    }
                    return;
                }

                // example: 'password confirmation doesn't match'
                const validationError = responseData?.errors?.full_messages?.[0];
                // example: 'Unauthorized'
                const apiError = responseData?.errors?.[0];
                const message = validationError || apiError || 'An unknown error occurred. Please try again.';
                setError({ error: true, message });
            });
    }, [token, history, signOut, mfaCode]);

    return (
        <div>
            <TokenDisplay
                header="Reset Delve Password"
                error={error}
                tokenAction={resetPassword}
                buttonText="Reset Password"
                mfaRequired={mfaRequired}
                mfaCode={mfaCode}
                onMfaCodeChange={(e) => setMfaCode(e.target.value)}
            />
        </div>
    );
};

export default PasswordResetContainer;